import { useEffect, useRef, useState, type CSSProperties, type PointerEvent, type ReactNode } from "react";
import { motion, useInView, useMotionValue, useReducedMotion, useSpring, useTransform } from "framer-motion";

/*
 * Shared pieces for the live Quilt Pro screens on the features page. Each screen is drawn on a
 * 1200 x 960 artboard (the size of the design files), scaled to fit, and stood up in 3D.
 */

export const ARTBOARD_W = 1200;
export const ARTBOARD_H = 960;
const BASE_TILT = 4; // degrees the screen leans back at rest
const MAX_TILT = 5;

/** Scales the fixed-size artboard to the width of its container. */
function useFitScale() {
    const ref = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / ARTBOARD_W));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return { ref, scale };
}

/** Anything that looks clickable: springs down when pressed and sends out a ring. Does nothing else unless given onClick. */
export function Clicky({
    children,
    className = "",
    style,
    ring = "rounded-full",
    onClick,
}: {
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
    ring?: string;
    onClick?: () => void;
}) {
    const [pulses, setPulses] = useState<number[]>([]);
    // Positioned for the ring, unless the caller already places it absolutely
    const position = /\b(absolute|fixed)\b/.test(className) ? "" : "relative";
    return (
        <motion.div
            className={`${position} cursor-pointer select-none ${className}`}
            style={style}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 600, damping: 25 }}
            onTapStart={() => setPulses((p) => [...p, Date.now()])}
            onTap={onClick}
        >
            {children}
            {pulses.map((id) => (
                <motion.span
                    key={id}
                    aria-hidden
                    className={`pointer-events-none absolute inset-0 ${ring} ring-2 ring-purple-500/60`}
                    initial={{ opacity: 0.9, scale: 1 }}
                    animate={{ opacity: 0, scale: 1.15 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    onAnimationComplete={() => setPulses((p) => p.filter((x) => x !== id))}
                />
            ))}
        </motion.div>
    );
}

/** The round, frosted play button laid over a video frame. */
export function PlayButton({ size = 90 }: { size?: number }) {
    return (
        <span
            className="flex items-center justify-center rounded-full bg-black/25 text-white backdrop-blur-md"
            style={{ width: size, height: size }}
        >
            <svg viewBox="0 0 24 24" className="ml-[6%] w-[36%] fill-current">
                <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5Z" />
            </svg>
        </span>
    );
}

/** A pretend mouse pointer for the autoplay demos. x and y are where its tip lands on the artboard. */
export function Pointer({ x, y, pressing, visible }: { x: number; y: number; pressing: boolean; visible: boolean }) {
    return (
        <motion.svg
            aria-hidden
            viewBox="0 0 24 24"
            className="pointer-events-none absolute left-0 top-0 z-30 size-12 drop-shadow-[0_4px_8px_rgba(0,0,0,0.35)]"
            initial={false}
            animate={{ x: x - 10, y: y - 6, opacity: visible ? 1 : 0, scale: pressing ? 0.8 : 1 }}
            transition={{ x: { duration: 1, ease: [0.65, 0, 0.35, 1] }, y: { duration: 1, ease: [0.65, 0, 0.35, 1] }, opacity: { duration: 0.3 }, scale: { duration: 0.12 } }}
        >
            <path d="M5 3l14 7.5-6.2 1.6L9.6 18 5 3Z" fill="white" stroke="#111" strokeWidth="1.4" strokeLinejoin="round" />
        </motion.svg>
    );
}

/** The rounded "screenshot" itself: white to grey in light mode, the site's purple-black in dark mode. */
export function ScreenFrame({ children }: { children: ReactNode }) {
    return (
        <div className="relative h-full w-full overflow-hidden rounded-[64px] border border-zinc-200 bg-[linear-gradient(135deg,#FFFFFF_25%,#E4E4E7)] dark:border-purple-300/10 dark:bg-[radial-gradient(90%_70%_at_100%_0%,rgba(139,31,166,0.16),transparent_60%),linear-gradient(135deg,#05050C_25%,#1B1730_115%)]">
            {children}
        </div>
    );
}

/**
 * The screen, standing in 3D: leans back a little, follows the pointer, settles in when it scrolls
 * into view. Sized so a heading and the whole screen fit in one desktop viewport.
 */
export function ScreenStage({
    label,
    reserve = "19rem",
    children,
}: {
    label: string;
    /** Viewport height kept free for the heading and anything under the screen */
    reserve?: string;
    children: (ctx: { scale: number; inView: boolean }) => ReactNode;
}) {
    const { ref: fitRef, scale } = useFitScale();
    const stage = useRef<HTMLDivElement>(null);
    const inView = useInView(stage, { once: true, amount: 0.3 });
    const reduceMotion = useReducedMotion();

    // Pointer position across the stage, -0.5 to 0.5; the lean never goes past MAX_TILT
    const px = useMotionValue(0);
    const py = useMotionValue(0);
    const spring = { stiffness: 120, damping: 20, mass: 0.6 };
    const rotateX = useSpring(useTransform(py, [-0.5, 0, 0.5], [MAX_TILT, BASE_TILT, BASE_TILT - 1.5]), spring);
    const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-2.5, 2.5]), spring);

    const onMove = (e: PointerEvent) => {
        if (reduceMotion || e.pointerType !== "mouse" || !stage.current) return;
        const r = stage.current.getBoundingClientRect();
        px.set((e.clientX - r.left) / r.width - 0.5);
        py.set((e.clientY - r.top) / r.height - 0.5);
    };
    const onLeave = () => {
        px.set(0);
        py.set(0);
    };

    return (
        <div
            ref={stage}
            className="relative mx-auto w-full [perspective:1800px]"
            // 80% of the design width, and never taller than the viewport left after the heading
            style={{ maxWidth: `min(${ARTBOARD_W * 0.8}px, calc((100svh - ${reserve}) * ${ARTBOARD_W / ARTBOARD_H}))` }}
            onPointerMove={onMove}
            onPointerLeave={onLeave}
        >
            {/* Glow behind the screen */}
            <div aria-hidden className="pointer-events-none absolute -inset-x-10 -inset-y-6 -z-10 rounded-[30%] bg-[radial-gradient(ellipse_at_center,rgba(140,0,226,0.18),rgba(0,236,237,0.06)_45%,transparent_70%)] blur-2xl dark:bg-[radial-gradient(ellipse_at_center,rgba(140,0,226,0.35),rgba(0,236,237,0.08)_45%,transparent_70%)]" />

            {/* Entrance: rises and tips back into place */}
            <motion.div
                className="[perspective:1800px]"
                initial={{ opacity: 0, y: 60, rotateX: 10 }}
                animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            >
                <motion.div
                    ref={fitRef}
                    role="img"
                    aria-label={label}
                    className="relative w-full origin-bottom"
                    style={{ height: ARTBOARD_H * scale, rotateX: reduceMotion ? BASE_TILT : rotateX, rotateY: reduceMotion ? 0 : rotateY }}
                >
                    <div aria-hidden className="absolute left-0 top-0 origin-top-left" style={{ width: ARTBOARD_W, height: ARTBOARD_H, transform: `scale(${scale})` }}>
                        {children({ scale, inView })}
                    </div>
                </motion.div>
            </motion.div>

            {/* Soft shadow on the "floor" */}
            <div aria-hidden className="pointer-events-none mx-auto -mt-4 h-12 w-4/5 rounded-[50%] bg-black/15 blur-2xl dark:bg-black/60" />
        </div>
    );
}
